import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Shield,
  FileText,
  Eye,
  Users,
  TrendingUp,
  Vote,
  Ban,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface Right {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  importance: 'critical' | 'high' | 'medium';
  fullParticipation: 'retained' | 'lost' | 'modified';
  partialParticipation: 'retained' | 'lost' | 'modified';
  noParticipation: 'retained' | 'lost' | 'modified';
  details: {
    fullParticipation: string;
    partialParticipation: string;
    noParticipation: string;
  };
}

const preferredRights: Right[] = [
  {
    id: 'anti-dilution',
    name: 'Anti-Dilution Protection',
    icon: <Shield className="h-4 w-4" />,
    description: 'Protection against ownership dilution in down rounds through price adjustments',
    importance: 'critical',
    fullParticipation: 'retained',
    partialParticipation: 'modified',
    noParticipation: 'lost',
    details: {
      fullParticipation: 'Full weighted-average anti-dilution protection maintained',
      partialParticipation: 'Partial protection based on participation percentage',
      noParticipation: 'All anti-dilution rights terminated',
    },
  },
  {
    id: 'liquidation-preference',
    name: 'Liquidation Preference',
    icon: <TrendingUp className="h-4 w-4" />,
    description: 'Priority in receiving proceeds upon exit before common stockholders',
    importance: 'critical',
    fullParticipation: 'retained',
    partialParticipation: 'modified',
    noParticipation: 'lost',
    details: {
      fullParticipation: '1x liquidation preference retained on all shares',
      partialParticipation: 'Preference reduced proportionally or converted to common',
      noParticipation: 'Converted to common stock - no preference',
    },
  },
  {
    id: 'rofr',
    name: 'Right of First Refusal (ROFR)',
    icon: <FileText className="h-4 w-4" />,
    description: 'First opportunity to purchase shares before other investors in future rounds',
    importance: 'high',
    fullParticipation: 'retained',
    partialParticipation: 'retained',
    noParticipation: 'lost',
    details: {
      fullParticipation: 'Full pro-rata ROFR rights maintained',
      partialParticipation: 'ROFR rights maintained but priority may be reduced',
      noParticipation: 'ROFR rights permanently forfeited',
    },
  },
  {
    id: 'pro-rata',
    name: 'Pro-Rata Rights',
    icon: <Users className="h-4 w-4" />,
    description: 'Right to maintain ownership percentage in future funding rounds',
    importance: 'high',
    fullParticipation: 'retained',
    partialParticipation: 'modified',
    noParticipation: 'lost',
    details: {
      fullParticipation: 'Full pro-rata participation rights in all future rounds',
      partialParticipation: 'Pro-rata rights reduced to participation percentage',
      noParticipation: 'All future participation rights eliminated',
    },
  },
  {
    id: 'information',
    name: 'Information Rights',
    icon: <Eye className="h-4 w-4" />,
    description: 'Access to company financials, board materials, and operating metrics',
    importance: 'high',
    fullParticipation: 'retained',
    partialParticipation: 'retained',
    noParticipation: 'modified',
    details: {
      fullParticipation: 'Full quarterly reports, annual audits, and board observer access',
      partialParticipation: 'Maintain standard reporting but may lose board observer rights',
      noParticipation: 'Limited to basic shareholder communications only',
    },
  },
  {
    id: 'voting',
    name: 'Super-Voting Rights',
    icon: <Vote className="h-4 w-4" />,
    description: 'Enhanced voting power on major corporate decisions and protective provisions',
    importance: 'critical',
    fullParticipation: 'retained',
    partialParticipation: 'modified',
    noParticipation: 'lost',
    details: {
      fullParticipation: 'Full class voting rights and veto provisions maintained',
      partialParticipation: 'Voting power reduced proportionally to common equivalent',
      noParticipation: 'Converted to common voting - no protective provisions',
    },
  },
  {
    id: 'drag-along',
    name: 'Drag-Along Protection',
    icon: <Shield className="h-4 w-4" />,
    description: 'Protection from being forced to sell shares in company sale',
    importance: 'medium',
    fullParticipation: 'retained',
    partialParticipation: 'retained',
    noParticipation: 'lost',
    details: {
      fullParticipation: 'Can participate in or block drag-along provisions',
      partialParticipation: 'Maintains ability to negotiate sale terms',
      noParticipation: 'Subject to majority drag-along without protection',
    },
  },
  {
    id: 'registration',
    name: 'Registration Rights',
    icon: <FileText className="h-4 w-4" />,
    description: 'Ability to force company registration for public offering or liquidity event',
    importance: 'high',
    fullParticipation: 'retained',
    partialParticipation: 'retained',
    noParticipation: 'modified',
    details: {
      fullParticipation: 'Full demand and piggyback registration rights',
      partialParticipation: 'Piggyback rights maintained, demand rights may be limited',
      noParticipation: 'Piggyback only, subject to cutback provisions',
    },
  },
  {
    id: 'redemption',
    name: 'Redemption Rights',
    icon: <TrendingUp className="h-4 w-4" />,
    description: 'Right to demand company repurchase shares after specified period',
    importance: 'medium',
    fullParticipation: 'retained',
    partialParticipation: 'modified',
    noParticipation: 'lost',
    details: {
      fullParticipation: 'Full redemption rights at original preference value',
      partialParticipation: 'Redemption at reduced preference or common value',
      noParticipation: 'No redemption rights - must find market buyer',
    },
  },
  {
    id: 'board',
    name: 'Board Rights',
    icon: <Users className="h-4 w-4" />,
    description: 'Board seat or observer rights to influence company direction',
    importance: 'high',
    fullParticipation: 'retained',
    partialParticipation: 'modified',
    noParticipation: 'lost',
    details: {
      fullParticipation: 'Board seat or observer status maintained',
      partialParticipation: 'May retain observer rights but lose board seat',
      noParticipation: 'All board participation rights terminated',
    },
  },
];

const StatusIcon = ({ status }: { status: 'retained' | 'lost' | 'modified' }) => {
  switch (status) {
    case 'retained':
      return <CheckCircle2 className="h-5 w-5 text-success" />;
    case 'lost':
      return <XCircle className="h-5 w-5 text-destructive" />;
    case 'modified':
      return <AlertTriangle className="h-5 w-5 text-warning" />;
  }
};

const StatusBadge = ({ status }: { status: 'retained' | 'lost' | 'modified' }) => {
  const variants = {
    retained: 'default',
    lost: 'destructive',
    modified: 'secondary',
  } as const;

  const labels = {
    retained: 'Retained',
    lost: 'Lost',
    modified: 'Modified',
  };

  return (
    <Badge variant={variants[status]} className="text-xs">
      {labels[status]}
    </Badge>
  );
};

const ImportanceBadge = ({ importance }: { importance: 'critical' | 'high' | 'medium' }) => {
  const variants = {
    critical: 'destructive',
    high: 'default',
    medium: 'secondary',
  } as const;

  const labels = {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
  };

  return (
    <Badge variant={variants[importance]} className="text-xs">
      {labels[importance]}
    </Badge>
  );
};

export function RightsComparisonMatrix() {
  const scenarios = [
    {
      key: 'fullParticipation' as const,
      label: 'Full Pro-Rata Participation',
      description: 'Invest full pro-rata share',
    },
    {
      key: 'partialParticipation' as const,
      label: 'Partial Participation',
      description: 'Invest less than pro-rata',
    },
    {
      key: 'noParticipation' as const,
      label: 'No Participation',
      description: 'Decline to participate',
    },
  ];

  const calculateImpact = () => {
    const totalRights = preferredRights.length;
    return scenarios.map((scenario) => {
      const retained = preferredRights.filter((r) => r[scenario.key] === 'retained').length;
      const modified = preferredRights.filter((r) => r[scenario.key] === 'modified').length;
      const lost = preferredRights.filter((r) => r[scenario.key] === 'lost').length;

      return {
        scenario: scenario.label,
        retained,
        modified,
        lost,
        retainedPct: Math.round((retained / totalRights) * 100),
        effectiveRetention: Math.round(((retained + modified * 0.5) / totalRights) * 100),
      };
    });
  };

  const impact = calculateImpact();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Preferred Rights Comparison Matrix
          </CardTitle>
          <CardDescription>
            Comprehensive analysis of how pay-to-play provisions affect your preferred shareholder
            rights
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Impact Summary */}
          <div className="grid md:grid-cols-3 gap-4">
            {impact.map((item, idx) => (
              <Card key={idx} className="border-muted">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">{item.scenario}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">{item.effectiveRetention}%</span>
                    <span className="text-sm text-muted-foreground">effective retention</span>
                  </div>
                  <div className="flex gap-2 text-xs">
                    <span className="text-success">{item.retained} retained</span>
                    <span className="text-warning">{item.modified} modified</span>
                    <span className="text-destructive">{item.lost} lost</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Critical Warning */}
          <Alert variant="destructive">
            <Ban className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Pay-to-Play Warning:</strong> Failing to participate in future rounds can
              result in permanent loss of critical investor protections, effectively converting your
              preferred shares to common stock.
            </AlertDescription>
          </Alert>

          {/* Rights Matrix Table */}
          <div className="rounded-lg border">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-3 text-left text-sm font-semibold min-w-[240px]">
                      Preferred Right
                    </th>
                    <th className="p-3 text-center text-sm font-semibold min-w-[140px]">
                      Importance
                    </th>
                    {scenarios.map((scenario) => (
                      <th
                        key={scenario.key}
                        className="p-3 text-center text-sm font-semibold min-w-[160px]"
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span>{scenario.label.split(' ')[0]}</span>
                          <span className="font-normal text-xs text-muted-foreground">
                            {scenario.label.split(' ').slice(1).join(' ')}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preferredRights.map((right, idx) => (
                    <tr key={right.id} className={idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                      <td className="p-3">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-start gap-2 cursor-help">
                                <div className="mt-0.5">{right.icon}</div>
                                <div>
                                  <div className="font-medium text-sm">{right.name}</div>
                                  <div className="text-xs text-muted-foreground line-clamp-2">
                                    {right.description}
                                  </div>
                                </div>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <p className="text-sm">{right.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                      <td className="p-3 text-center">
                        <ImportanceBadge importance={right.importance} />
                      </td>
                      {scenarios.map((scenario) => (
                        <td key={scenario.key} className="p-3">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex flex-col items-center gap-2 cursor-help">
                                  <StatusIcon status={right[scenario.key]} />
                                  <StatusBadge status={right[scenario.key]} />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs">
                                <p className="text-sm font-medium mb-1">{scenario.label}</p>
                                <p className="text-xs">{right.details[scenario.key]}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Legend */}
          <Card className="bg-muted/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Info className="h-4 w-4" />
                Understanding the Status Indicators
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <span className="font-medium">Retained:</span>
                <span className="text-muted-foreground">
                  Right is fully preserved without changes
                </span>
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-warning" />
                <span className="font-medium">Modified:</span>
                <span className="text-muted-foreground">
                  Right is partially retained but with reduced benefits
                </span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="h-4 w-4 text-destructive" />
                <span className="font-medium">Lost:</span>
                <span className="text-muted-foreground">
                  Right is completely forfeited and cannot be recovered
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Educational Content */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              <strong>Strategic Consideration:</strong> Pay-to-play provisions are designed to
              ensure ongoing investor support during challenging periods. While they may seem
              punitive, they align investor interests with company success and prevent free-riding.
              Consider your conviction level and ability to participate before accepting investments
              with these terms.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
